import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { resolveMedia } from "../data/projects";
import { GALLERY_EN } from "../i18n";
import { useLang } from "../context/LangContext";

const PHOTOS = [
  {
    id: "night-pour",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/uutmj6d6_IMG-20260923-WA0065.jpg",
    caption: "أعمال الصب الليلية — أبراج الجادرية",
  },
  {
    id: "towers-model",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/ss2u1l2d_IMG-20260923-WA0066.jpg",
    caption: "الموديل الهندسي — أبراج الجادرية",
  },
  {
    id: "surveying",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/4xvfuntk_IMG-20260923-WA0067.jpg",
    caption: "أعمال المساحة الليلية بجهاز المحطة الشاملة",
  },
  {
    id: "slabdeck",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/zhcnk1u2_IMG-20260923-WA0068.jpg",
    caption: "الشدات السقفية SLABDECK قيد التركيب",
  },
  {
    id: "rebar-aerial",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/klbsvdmt_IMG-20260923-WA0069.jpg",
    caption: "تسليح السقوف فوق سماء المدينة",
  },
];

const GalleryRow = ({ reverse, onOpen, photos, lang }) => {
  const row = [...photos, ...photos];
  return (
    <div className="overflow-hidden py-3" dir="ltr">
      <div className={`marquee-track items-center gap-5 px-3 ${reverse ? "marquee-reverse" : ""}`}>
        {row.map((p, i) => (
          <button
            key={`${p.id}-${i}`}
            data-testid={i < photos.length ? `gallery-photo-${p.id}` : undefined}
            onClick={() => onOpen(p)}
            className="group relative shrink-0 h-56 lg:h-72 rounded-lg overflow-hidden clip-frame border border-[#E8DFC5] focus:outline-none focus:ring-2 focus:ring-emerald-500"
            dir={lang === "ar" ? "rtl" : "ltr"}
          >
            <img
              src={p.src}
              alt={p.caption}
              loading="lazy"
              className="h-full w-auto object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#14100A]/90 to-transparent px-4 pt-10 pb-3 text-sm font-semibold text-white opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 text-right">
              {p.caption}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default function Gallery() {
  const { lang, t } = useLang();
  const [active, setActive] = useState(null);
  const [photos, setPhotos] = useState(PHOTOS);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (Array.isArray(d) && d.length) {
          setPhotos(d.map((g, i) => ({ id: g.id || `g${i}`, src: resolveMedia(g.src), caption: g.caption })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const display = photos.map((p) => ({
    ...p,
    caption: lang === "en" ? GALLERY_EN[p.caption] || p.caption : p.caption,
  }));

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 lg:py-32 bg-white/40 border-y border-[#E8DFC5] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          index={t.gallery.index}
          eyebrow={t.gallery.eyebrow}
          title={t.gallery.title}
          description={t.gallery.desc}
        />
      </div>

      <Reveal>
        <div className="flex flex-col gap-2" data-testid="gallery-rows">
          <GalleryRow onOpen={setActive} photos={display} lang={lang} />
          <GalleryRow reverse onOpen={setActive} photos={display} lang={lang} />
        </div>
      </Reveal>

      <Reveal delay={0.15} className="max-w-7xl mx-auto px-5 lg:px-8 mt-10">
        <p className="flex items-center gap-3 text-sm text-stone-500">
          <Camera className="w-4 h-4 text-emerald-500" />
          {t.gallery.note}
        </p>
      </Reveal>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[60] bg-[#14100A]/90 backdrop-blur-md flex items-center justify-center p-5"
          >
            <motion.figure
              initial={{ scale: 0.9, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 16 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl w-full"
            >
              <img
                src={active.src}
                alt={active.caption}
                className="w-full max-h-[75vh] object-contain rounded-lg clip-frame border border-[#E8DFC5] bg-white"
              />
              <figcaption className="mt-4 text-center font-heading font-semibold text-base sm:text-lg text-white">
                {active.caption}
              </figcaption>
              <button
                data-testid="gallery-lightbox-close"
                onClick={() => setActive(null)}
                className="absolute -top-4 -left-4 w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center transition-transform duration-300 hover:rotate-90"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={2.6} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
